import React, { ChangeEvent, useContext, useMemo, useState } from 'react';

import ScorecardContext from 'contexts/ScorecardContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import aguid from 'lib/aguid';
import STORE from 'store';
import isDefined from 'isDefined';
import useGetDatasetDefinition from 'hooks/useGetDatasetDefinition';
import useScorecardDatasets from 'screens/ScorecardShow/useScorecardDatasets';
import { buildQuickFilterFieldRedList } from 'screens/ScorecardShow/scorecardQuickFilterFields';

import NewQuickFilterForm from '../../CustomerLaneCommitsBoard/Settings/QuickFilters/NewQuickFilterForm/NewQuickFilterForm';

const NewScorecardQuickFilterFormContainer = ({
  close,
}: {
  close: () => void;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { scorecard, updateScorecard } = useContext(ScorecardContext);
  const getDatasetDefinition = useGetDatasetDefinition();
  const scorecardDatasets = useScorecardDatasets(scorecard);
  const [name, setName] = useState<string>('');
  const [drillDowns, setDrillDowns] = useState<FilterPlateType[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Requirement 1: only fields common to every dataset on the scorecard may be
  // picked. We ban the rest via the FieldPicker's existing fieldRedList prop.
  const fieldRedList = useMemo(
    () =>
      buildQuickFilterFieldRedList({
        datasets: scorecardDatasets.map(getDatasetDefinition).filter(isDefined),
      }),
    [scorecardDatasets, getDatasetDefinition],
  );

  const onNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const isValid = drillDowns.length !== 0;

  const onSave = async () => {
    if (!isValid) {
      return;
    }

    const filter: LegacySavedFilter = {
      version: '2',
      id: aguid(),
      scope: [], // scorecard quick filters are not scoped to specific entities
      drillDowns,
      source: `scorecard: ${scorecard.id}`, // namespaced so useQuickFilters queries only this scorecard's filters
      name,
      dateField: 'date',
    };

    setIsSaving(true);
    await STORE.filterDefinitions
      .getLegacySavedFiltersRef({ accountId: selectedAccountId })
      .doc(filter.id)
      .set(filter);

    await updateScorecard({
      ...scorecard,
      quickFilterOrder: [...(scorecard.quickFilterOrder || []), filter.id],
    });

    setIsSaving(false);
    close();
  };

  return (
    <FilterPlatesProvider
      drillDowns={drillDowns}
      setDrillDowns={setDrillDowns}
      dataTypes={scorecardDatasets}
      fieldRedList={fieldRedList}
      isSavedFiltersSelectorDisabled
    >
      <NewQuickFilterForm
        name={name}
        onNameChanged={onNameChanged}
        close={close}
        onSave={onSave}
        isSaving={isSaving}
        isValid={isValid}
      />
    </FilterPlatesProvider>
  );
};

export default NewScorecardQuickFilterFormContainer;
